import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import pluginService from "api/pluginService";
import ChooseSigningCertificate from "components/ChooseSigningCertificate";
import { ICertificate } from "dto/ICertificate";
import {
  IBudgetEngagementDocument,
  engagementElementLabel,
} from "dto/Budget/IBudgetEngagementDocument";
import { ENotificationType, notification } from "utils/notification";
import {
  errorMessage,
  signEngagementDocumentElement,
} from "utils/signEngagementDocument";

/**
 * Podpis XAdES elementów dokumentu wniosku przez wtyczkę: wybór certyfikatu
 * (ChooseSigningCertificate) → podpis → zapis w BE. `requestSign(elementId)` otwiera
 * wybór certyfikatu; `modal` trzeba wyrenderować w drzewie.
 */
export const useEngagementSigning = (
  engagementId: string,
  onSigned?: (doc: IBudgetEngagementDocument) => void,
) => {
  const queryClient = useQueryClient();
  const { data: certsData, isError: pluginUnavailable } =
    pluginService.useGetCerificates();
  const certs = useMemo(() => certsData?.data ?? [], [certsData]);

  const [elementId, setElementId] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);

  const sign = useCallback(
    async (cert: ICertificate) => {
      const target = elementId;
      setElementId(null);
      if (!target) return;
      setSigning(true);
      try {
        const doc = await signEngagementDocumentElement(
          engagementId,
          target,
          cert,
        );
        queryClient.setQueryData(
          ["budget.engagementDocument", engagementId],
          doc,
        );
        queryClient.invalidateQueries({
          predicate: (q) =>
            String(q.queryKey[0] ?? "").startsWith("acceptance"),
        });
        notification({
          type: ENotificationType.SUCCESS,
          title: "Podpisano",
          text: `Podpis elektroniczny dołączony do dokumentu (${engagementElementLabel(target)}).`,
        });
        onSigned?.(doc);
      } catch (e) {
        notification({
          type: ENotificationType.ERROR,
          title: "Podpis nie powiódł się",
          text: errorMessage(e, "Nie udało się podpisać dokumentu."),
        });
      } finally {
        setSigning(false);
      }
    },
    [elementId, engagementId, onSigned, queryClient],
  );

  const modal = elementId ? (
    <ChooseSigningCertificate
      certs={certs}
      allowChooseMethod={false}
      toggle={() => setElementId(null)}
      onClick={sign}
    />
  ) : null;

  return {
    requestSign: (id: string) => setElementId(id),
    signing,
    /** Wtyczka niedostępna (brak listy certyfikatów) — podpis elektroniczny niemożliwy. */
    pluginUnavailable: pluginUnavailable || (!!certsData && certs.length === 0),
    modal,
  };
};
